import { useDeploymentCapabilities } from '@/app-shell/deployment-capabilities';
import { useTranslatorNavEnabled } from '@/features/flags';
import { useIsEntitlementEnabled } from '@/hooks/entitlements/useIsEntitlementEnabled';
import { isTauri } from '@/tauri/utils';
import { ENTITLEMENT } from '@heidi/workspace/contracts';

// The entitlement is the prod seat lever; below prod the nav flag alone gates
// so internal testers need no grant. Keys off the RAW env var, fail closed:
// getEnvironment() coerces unset/unknown values to DEV, which would fail open,
// and onprem (a production deployment) must keep the gate.
const ENTITLEMENT_BYPASS_ENVIRONMENTS: ReadonlySet<string> = new Set([
  'localhost',
  'development',
  'azure_dev',
  'staging',
]);

export function useTranslatorAccess(): {
  isTranslatorShown: boolean;
  isPending: boolean;
} {
  const { isEnabled, isPending } = useIsEntitlementEnabled(ENTITLEMENT.TRANSLATOR);
  const flagOn = useTranslatorNavEnabled();
  // On-prem: hidden once an admin removes the interpreter, whatever the
  // (create-only) entitlement grant says. Always present on cloud.
  const { components, isPending: isCapabilitiesPending } = useDeploymentCapabilities();

  // Web-only: the tab drives browser getUserMedia/MediaRecorder, which are
  // not the capture authority on the Tauri desktop app.
  if (isTauri()) {
    return { isTranslatorShown: false, isPending: false };
  }

  // The query keeps running (and isPending flowing) in every environment so
  // the redirect guard keeps its loading window; only the decision bypasses.
  // FE display gate only — no translator endpoint enforces the entitlement
  // server-side, so the bypass changes no backend guarantee.
  const entitlementBypassed = ENTITLEMENT_BYPASS_ENVIRONMENTS.has(
    process.env.NEXT_PUBLIC_ENVIRONMENT ?? ''
  );

  return {
    isTranslatorShown:
      !isCapabilitiesPending &&
      components.interpreter &&
      flagOn &&
      (entitlementBypassed || isEnabled),
    isPending: isPending || isCapabilitiesPending,
  };
}
